import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Cloud,
  CloudOff,
  LogIn,
  LogOut,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Settings,
  Trash2,
  WifiOff,
  X,
} from 'lucide-react'
import { login, logout, serverStatus, setup, syncNow, type SyncState } from './api'
import {
  addCategory,
  addTask,
  getSetting,
  initialiseDatabase,
  moveCategory,
  removeCategory,
  removeTask,
  setSetting,
  taskDataQuery,
  updateCategory,
  updateTask,
} from './db'
import { buildCategoryTree, countOpenTasks, flattenCategories } from './tree'
import type { Category, CategoryNode, Task } from './types'
import { registerTaskTools } from './webmcp'

const COLOURS = ['#2f7cf6', '#7c5cff', '#15a085', '#ef8354', '#d1495b', '#6b7c93']

function Modal({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  useEffect(() => {
    const close = (event: globalThis.KeyboardEvent) => event.key === 'Escape' && onClose()
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [onClose])

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-header">
          <h2>{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label="Close"><X size={20} /></button>
        </header>
        {children}
      </section>
    </div>
  )
}

function QuickAdd({ categories, preferredCategory, onAdded }: { categories: CategoryNode[]; preferredCategory: string | null; onAdded: () => void }) {
  const flat = useMemo(() => flattenCategories(categories), [categories])
  const [categoryId, setCategoryId] = useState(preferredCategory || flat[0]?.category.id || '')
  const [title, setTitle] = useState('')
  const [details, setDetails] = useState('')
  const titleRef = useRef<HTMLInputElement>(null)
  const detailsRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (preferredCategory) {
      setCategoryId(preferredCategory)
      requestAnimationFrame(() => titleRef.current?.focus())
    } else if (!categoryId && flat[0]) setCategoryId(flat[0].category.id)
  }, [preferredCategory, categoryId, flat])

  const submit = async () => {
    if (!title.trim() || !categoryId) return
    await addTask(categoryId, title, details)
    setTitle('')
    setDetails('')
    onAdded()
    requestAnimationFrame(() => titleRef.current?.focus())
  }

  return (
    <section className="quick-add" aria-label="Add a task">
      <div className="quick-add-mark"><Plus size={22} /></div>
      <div className="quick-add-fields">
        <input
          id="quick-add-title"
          ref={titleRef}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              detailsRef.current?.focus()
            }
          }}
          enterKeyHint="next"
          placeholder="What needs doing?"
          aria-label="Task title"
        />
        <input
          ref={detailsRef}
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void submit()
            }
          }}
          enterKeyHint="done"
          placeholder="Add a short detail, then press Enter"
          aria-label="Task details"
        />
      </div>
      <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)} aria-label="Task category">
        {flat.map(({ category, depth }) => (
          <option value={category.id} key={category.id}>{`${'— '.repeat(depth)}${category.name}`}</option>
        ))}
      </select>
      <button className="primary-button add-button" onClick={() => void submit()} disabled={!title.trim() || !categoryId}>
        <Plus size={18} /> Add
      </button>
    </section>
  )
}

function TaskRow({ task, categoryColour, onEdit, onChanged }: { task: Task; categoryColour: string; onEdit: () => void; onChanged: () => void }) {
  return (
    <div className={`task-row ${task.completed ? 'task-complete' : ''}`}>
      <button
        className="task-check"
        style={{ '--category-colour': categoryColour } as React.CSSProperties}
        onClick={() => void updateTask(task, { completed: !task.completed }).then(onChanged)}
        aria-label={task.completed ? `Mark ${task.title} incomplete` : `Complete ${task.title}`}
      >
        {task.completed && <Check size={16} strokeWidth={3} />}
      </button>
      <button className="task-main" onClick={onEdit}>
        <span className="task-title">{task.title}</span>
        {task.details && <span className="task-details">{task.details}</span>}
      </button>
      <button className="task-menu" onClick={onEdit} aria-label={`Edit ${task.title}`}><MoreHorizontal size={20} /></button>
    </div>
  )
}

function CategoryBranch({
  node,
  categories,
  tasks,
  collapsed,
  showCompleted,
  onToggle,
  onAdd,
  onEditTask,
  onChanged,
  depth = 0,
}: {
  node: CategoryNode
  categories: Category[]
  tasks: Task[]
  collapsed: Set<string>
  showCompleted: boolean
  onToggle: (id: string) => void
  onAdd: (id: string) => void
  onEditTask: (task: Task) => void
  onChanged: () => void
  depth?: number
}) {
  const isCollapsed = collapsed.has(node.id)
  const directTasks = tasks
    .filter((task) => task.categoryId === node.id && (showCompleted || !task.completed))
    .sort((a, b) => Number(a.completed) - Number(b.completed) || a.sortOrder - b.sortOrder)
  const openCount = countOpenTasks(node.id, categories, tasks)

  return (
    <section className={`category ${depth ? 'category-nested' : ''}`} style={{ '--depth': depth, '--category-colour': node.colour } as React.CSSProperties}>
      <div className="category-heading">
        <button className="category-toggle" onClick={() => onToggle(node.id)} aria-expanded={!isCollapsed}>
          {isCollapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
          <span className="category-dot" />
          <span>{node.name}</span>
          <span className="task-count">{openCount}</span>
        </button>
        <button className="category-add" onClick={() => onAdd(node.id)} aria-label={`Add task to ${node.name}`}><Plus size={19} /></button>
      </div>
      {!isCollapsed && (
        <div className="category-body">
          {directTasks.map((task) => (
            <TaskRow key={task.id} task={task} categoryColour={node.colour} onEdit={() => onEditTask(task)} onChanged={onChanged} />
          ))}
          {node.children.map((child) => (
            <CategoryBranch
              key={child.id}
              node={child}
              categories={categories}
              tasks={tasks}
              collapsed={collapsed}
              showCompleted={showCompleted}
              onToggle={onToggle}
              onAdd={onAdd}
              onEditTask={onEditTask}
              onChanged={onChanged}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function TaskEditor({ task, categories, onClose, onChanged }: { task: Task; categories: CategoryNode[]; onClose: () => void; onChanged: () => void }) {
  const flat = flattenCategories(categories)
  const [title, setTitle] = useState(task.title)
  const [details, setDetails] = useState(task.details)
  const [categoryId, setCategoryId] = useState(task.categoryId)

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!title.trim()) return
    await updateTask(task, { title, details, categoryId })
    onChanged()
    onClose()
  }

  return (
    <Modal title="Task details" onClose={onClose}>
      <form className="editor-form" onSubmit={(event) => void save(event)}>
        <label>Title<input value={title} onChange={(event) => setTitle(event.target.value)} autoFocus /></label>
        <label>Details<textarea value={details} onChange={(event) => setDetails(event.target.value)} rows={5} placeholder="Notes, links or anything useful" /></label>
        <label>Category<select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
          {flat.map(({ category, depth }) => <option key={category.id} value={category.id}>{`${'— '.repeat(depth)}${category.name}`}</option>)}
        </select></label>
        <div className="modal-actions split-actions">
          <button type="button" className="danger-button" onClick={() => void removeTask(task).then(() => { onChanged(); onClose() })}><Trash2 size={17} /> Delete</button>
          <div><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button className="primary-button" type="submit">Save task</button></div>
        </div>
      </form>
    </Modal>
  )
}

function CategorySettingsRow({ category, depth, canDelete, onAddChild, onDelete, onChanged }: {
  category: CategoryNode
  depth: number
  canDelete: boolean
  onAddChild: (id: string) => void
  onDelete: (category: Category) => void
  onChanged: () => void
}) {
  const [name, setName] = useState(category.name)
  const saveName = async () => {
    if (name.trim() && name.trim() !== category.name) {
      await updateCategory(category, { name })
      onChanged()
    } else setName(category.name)
  }

  return (
    <div className="category-setting" style={{ '--depth': depth } as React.CSSProperties}>
      <span className="tree-guide" />
      <input
        className="category-name-input"
        value={name}
        onChange={(event) => setName(event.target.value)}
        onBlur={() => void saveName()}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => event.key === 'Enter' && event.currentTarget.blur()}
        aria-label={`Category name: ${category.name}`}
      />
      <input
        className="colour-input"
        type="color"
        value={category.colour}
        onChange={(event) => void updateCategory(category, { colour: event.target.value }).then(onChanged)}
        aria-label={`Colour for ${category.name}`}
      />
      <div className="category-tools">
        <button onClick={() => void moveCategory(category, -1).then(onChanged)} aria-label={`Move ${category.name} up`}><ArrowUp size={16} /></button>
        <button onClick={() => void moveCategory(category, 1).then(onChanged)} aria-label={`Move ${category.name} down`}><ArrowDown size={16} /></button>
        <button onClick={() => onAddChild(category.id)} aria-label={`Add subcategory to ${category.name}`}><Plus size={16} /></button>
        <button className="danger-icon" disabled={!canDelete} onClick={() => onDelete(category)} aria-label={`Delete ${category.name}`}><Trash2 size={16} /></button>
      </div>
    </div>
  )
}

function CategorySettings({ categories, onClose, onChanged }: { categories: CategoryNode[]; onClose: () => void; onChanged: () => void }) {
  const flat = flattenCategories(categories)
  const [addingParent, setAddingParent] = useState<string | null | undefined>(undefined)
  const [newName, setNewName] = useState('')
  const [newColour, setNewColour] = useState(COLOURS[0])
  const [confirmDelete, setConfirmDelete] = useState<Category | null>(null)

  const add = async (event: FormEvent) => {
    event.preventDefault()
    if (!newName.trim()) return
    await addCategory(newName, addingParent ?? null, newColour)
    setNewName('')
    setAddingParent(undefined)
    setNewColour(COLOURS[Math.floor(Math.random() * COLOURS.length)])
    onChanged()
  }

  return (
    <Modal title="Categories" onClose={onClose} wide>
      <div className="settings-intro">Rename categories directly. Use <Plus size={15} /> to nest a subcategory.</div>
      <div className="category-settings-list">
        {flat.map(({ category, depth }) => (
          <CategorySettingsRow key={category.id} category={category} depth={depth} canDelete={flat.length > 1} onAddChild={setAddingParent} onDelete={setConfirmDelete} onChanged={onChanged} />
        ))}
      </div>
      {addingParent !== undefined ? (
        <form className="new-category-form" onSubmit={(event) => void add(event)}>
          <input type="color" value={newColour} onChange={(event) => setNewColour(event.target.value)} aria-label="New category colour" />
          <input value={newName} onChange={(event) => setNewName(event.target.value)} placeholder={addingParent ? 'Subcategory name' : 'Category name'} autoFocus />
          <button className="primary-button" type="submit">Add</button>
          <button className="secondary-button" type="button" onClick={() => setAddingParent(undefined)}>Cancel</button>
        </form>
      ) : (
        <button className="add-category-button" onClick={() => setAddingParent(null)}><Plus size={18} /> Add main category</button>
      )}
      {confirmDelete && (
        <div className="inline-confirm" role="alert">
          <CircleAlert size={21} />
          <div><strong>Remove {confirmDelete.name}?</strong><span>Its tasks and subcategories will move up one level.</span></div>
          <button className="secondary-button" onClick={() => setConfirmDelete(null)}>Cancel</button>
          <button className="danger-button" onClick={() => void removeCategory(confirmDelete).then(() => { setConfirmDelete(null); onChanged() })}>Remove</button>
        </div>
      )}
    </Modal>
  )
}

function AuthModal({ onClose, onAuthenticated }: { onClose: () => void; onAuthenticated: (username: string) => void }) {
  const [mode, setMode] = useState<'checking' | 'login' | 'setup' | 'unavailable'>('checking')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [setupCode, setSetupCode] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    serverStatus().then((status) => setMode(status.configured ? 'login' : 'setup')).catch(() => setMode('unavailable'))
  }, [])

  useEffect(() => registerTaskTools(), [])

  const submitAuth = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      const result = mode === 'setup' ? await setup(setupCode, username, password) : await login(username, password)
      onAuthenticated(result.username)
      onClose()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not sign in.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={mode === 'setup' ? 'Create your account' : 'Sync your tasks'} onClose={onClose}>
      {mode === 'checking' && <div className="loading-state"><RefreshCw className="spin" size={22} /> Checking your server…</div>}
      {mode === 'unavailable' && <div className="auth-unavailable"><WifiOff size={28} /><strong>Server unavailable</strong><p>Your tasks still save on this device. Try again after the web service is deployed.</p></div>}
      {(mode === 'login' || mode === 'setup') && (
        <form className="editor-form" onSubmit={(event) => void submitAuth(event)}>
          {mode === 'setup' && <label>Setup code<input value={setupCode} onChange={(event) => setSetupCode(event.target.value)} autoComplete="one-time-code" required /></label>}
          <label>Username<input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" required autoFocus={mode === 'login'} /></label>
          <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === 'setup' ? 'new-password' : 'current-password'} minLength={10} required /></label>
          {error && <div className="form-error"><CircleAlert size={17} />{error}</div>}
          <div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Stay offline</button><button className="primary-button" type="submit" disabled={busy}>{busy ? 'Please wait…' : mode === 'setup' ? 'Create account' : 'Sign in'}</button></div>
        </form>
      )}
    </Modal>
  )
}

export default function App() {
  const [categories, setCategories] = useState<Category[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [ready, setReady] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [showCompleted, setShowCompleted] = useState(false)
  const [preferredCategory, setPreferredCategory] = useState<string | null>(null)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [authOpen, setAuthOpen] = useState(false)
  const [username, setUsername] = useState('')
  const [syncState, setSyncState] = useState<SyncState>(navigator.onLine ? 'saved' : 'offline')

  const tree = useMemo(() => buildCategoryTree(categories), [categories])

  const performSync = useCallback(async () => {
    if (!navigator.onLine) {
      setSyncState('offline')
      return
    }
    setSyncState('syncing')
    try {
      const synced = await syncNow()
      setSyncState(synced ? 'saved' : 'saved')
    } catch {
      setSyncState('error')
    }
  }, [])

  const changed = useCallback(() => {
    setSyncState(navigator.onLine ? 'saved' : 'offline')
    window.setTimeout(() => void performSync(), 250)
  }, [performSync])

  useEffect(() => {
    void initialiseDatabase().then(async () => {
      const [savedCollapsed, savedShowCompleted, savedUsername] = await Promise.all([
        getSetting<string[]>('collapsed', []),
        getSetting('showCompleted', false),
        getSetting('username', ''),
      ])
      setCollapsed(new Set(savedCollapsed))
      setShowCompleted(savedShowCompleted)
      setUsername(savedUsername)
      setReady(true)
    })
    const subscription = taskDataQuery().subscribe({
      next: (data) => {
        setCategories(data.categories)
        setTasks(data.tasks)
      },
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    const online = () => void performSync()
    const offline = () => setSyncState('offline')
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    const interval = window.setInterval(() => void performSync(), 45_000)
    if (ready) void performSync()
    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
      window.clearInterval(interval)
    }
  }, [performSync, ready])

  const toggleCategory = (id: string) => {
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      void setSetting('collapsed', [...next])
      return next
    })
  }

  const syncLabel = syncState === 'syncing' ? 'Syncing' : syncState === 'offline' ? 'Offline' : syncState === 'error' ? 'Sync issue' : username ? 'Synced' : 'On device'

  if (!ready) return <main className="boot-screen"><div className="brand-mark"><Check size={26} /></div><span>Opening your tasks…</span></main>

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand"><div className="brand-mark"><Check size={21} /></div><div><h1>My Tasks</h1><span>Quick, clear, remembered.</span></div></div>
        <div className="top-actions">
          <button className={`sync-chip sync-${syncState}`} onClick={() => username ? void performSync() : setAuthOpen(true)}>
            {syncState === 'syncing' ? <RefreshCw className="spin" size={16} /> : syncState === 'offline' || syncState === 'error' ? <CloudOff size={16} /> : <Cloud size={16} />}
            <span>{syncLabel}</span>
          </button>
          {username ? (
            <button className="icon-button" onClick={() => void logout().then(() => { setUsername(''); setSyncState('saved') })} aria-label="Sign out"><LogOut size={20} /></button>
          ) : (
            <button className="icon-button" onClick={() => setAuthOpen(true)} aria-label="Sign in to sync"><LogIn size={20} /></button>
          )}
          <button className="icon-button" onClick={() => setSettingsOpen(true)} aria-label="Category settings"><Settings size={21} /></button>
        </div>
      </header>

      <main className="main-content">
        <div className="list-toolbar">
          <div><strong>Today</strong><span>{tasks.filter((task) => !task.completed).length} open task{tasks.filter((task) => !task.completed).length === 1 ? '' : 's'}</span></div>
          <label className="completed-toggle"><input type="checkbox" checked={showCompleted} onChange={(event) => { setShowCompleted(event.target.checked); void setSetting('showCompleted', event.target.checked) }} /><span>Show completed</span></label>
        </div>

        <QuickAdd categories={tree} preferredCategory={preferredCategory} onAdded={changed} />

        <div className="category-list">
          {tree.map((node) => (
            <CategoryBranch
              key={node.id}
              node={node}
              categories={categories}
              tasks={tasks}
              collapsed={collapsed}
              showCompleted={showCompleted}
              onToggle={toggleCategory}
              onAdd={(id) => { setPreferredCategory(null); requestAnimationFrame(() => setPreferredCategory(id)) }}
              onEditTask={setEditingTask}
              onChanged={changed}
            />
          ))}
        </div>

        {tasks.length === 0 && <div className="empty-state"><div><Check size={28} /></div><strong>A clear list</strong><p>Add your first task above. It saves as soon as you press Enter.</p></div>}
      </main>

      <footer><span>Changes save on this device immediately.</span>{username && <span>Signed in as {username}</span>}</footer>

      {editingTask && <TaskEditor task={editingTask} categories={tree} onClose={() => setEditingTask(null)} onChanged={changed} />}
      {settingsOpen && <CategorySettings categories={tree} onClose={() => setSettingsOpen(false)} onChanged={changed} />}
      {authOpen && <AuthModal onClose={() => setAuthOpen(false)} onAuthenticated={(name) => { setUsername(name); void performSync() }} />}
    </div>
  )
}
